import { colors } from "../../style";
import { Btn, IconButton } from "./buttons";
import { Spinner } from "./display";
import { Icon } from "./Icon";
import { ui } from "./styles";

const tones = {
  info: {
    background: colors.tealTint,
    border: colors.tealBorder,
    icon: colors.teal,
  },
  neutral: {
    background: colors.surface,
    border: colors.border,
    icon: colors.textMuted,
  },
  warning: {
    background: colors.amberTint,
    border: colors.amberBorder,
    icon: colors.amber,
  },
  error: {
    background: colors.redTint,
    border: colors.redBorder,
    icon: colors.red,
  },
};

// Inline banner for help text, quota warnings and errors
export function Notice({
  tone = "info",
  title,
  children,
  action,
  onDismiss,
  dismissLabel = "Dismiss",
  style,
}) {
  const t = tones[tone];
  return (
    <div
      role={tone === "error" ? "alert" : undefined}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        padding: "12px 14px",
        borderRadius: "12px",
        background: t.background,
        border: `1px solid ${t.border}`,
        color: colors.textStrong,
        fontSize: "14px",
        lineHeight: 1.45,
        ...style,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && (
          <div style={{ fontWeight: 600, color: colors.text }}>{title}</div>
        )}
        {children && (
          <div style={title ? { ...ui.muted, marginTop: "2px" } : undefined}>
            {children}
          </div>
        )}
      </div>
      {action}
      {onDismiss && (
        <IconButton
          icon="close"
          label={dismissLabel}
          title={undefined}
          onClick={onDismiss}
        />
      )}
    </div>
  );
}

// inline: a left-aligned muted line, for an empty list
export function EmptyState({ icon, title, children, action, inline, style }) {
  if (inline) {
    return (
      <p style={{ ...ui.muted, margin: "8px 4px", ...style }}>{children}</p>
    );
  }
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "8px",
        padding: "32px 16px",
        textAlign: "center",
        ...style,
      }}
    >
      {icon && <Icon name={icon} size={24} color={colors.textMuted} />}
      {title && (
        <div style={{ fontWeight: 600, color: colors.text }}>{title}</div>
      )}
      {children && (
        <div style={{ ...ui.muted, fontSize: "14px" }}>{children}</div>
      )}
      {action && <div style={{ marginTop: "6px" }}>{action}</div>}
    </div>
  );
}

export function LoadingState({ label, style }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
        padding: "32px 16px",
        ...ui.muted,
        fontSize: "14px",
        ...style,
      }}
    >
      <Spinner size={20} />
      {label}
    </div>
  );
}

// card: boxed like a list (default); otherwise centred, e.g. a page that
// failed to load
export function ErrorState({ children, onRetry, retryLabel, card = true }) {
  const retry = onRetry && <Btn onClick={onRetry}>{retryLabel}</Btn>;
  if (!card) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          alignItems: "center",
        }}
      >
        <p style={ui.muted}>{children}</p>
        {retry}
      </div>
    );
  }
  return (
    <div
      role="alert"
      style={{
        ...ui.card,
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        alignItems: "flex-start",
      }}
    >
      <p style={{ margin: 0 }}>{children}</p>
      {retry}
    </div>
  );
}

const bannerColors = {
  info: colors.teal,
  warning: colors.amber,
  error: colors.red,
};

// Status block with an icon; actions sit under the text
export function Banner({ tone = "info", icon, title, children, action }) {
  const color = bannerColors[tone];
  return (
    <div
      style={{
        display: "flex",
        gap: "12px",
        alignItems: "flex-start",
        padding: "14px 16px",
        borderRadius: "12px",
        background: `${color}14`,
        border: `1px solid ${color}4d`,
        fontSize: "14px",
        lineHeight: 1.6,
      }}
    >
      <Icon name={icon} size={18} color={color} style={{ marginTop: "2px" }} />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        {title && <strong style={{ color: colors.text }}>{title}</strong>}
        <div>{children}</div>
        {action}
      </div>
    </div>
  );
}
